import { StreamState, StreamNode } from "./partStore";
import { DESTINATION_LIST_DATA, TEMP_TRIP_DETAILS_DATA } from "../constant/dummyData";

export interface ProjectedActivity {
  id: string;
  time_of_the_day: string;
  activity_type: string;
  description: string;
  [key: string]: unknown;
}

export interface ProjectedDay {
  id: string;
  n: number | null;
  day: string;
  date: string;
  city_name: string;
  state: string;
  country: string;
  cc: string;
  lat: string;
  lng: string;
  day_itinerary_description: string;
  itinerary_by_time_of_the_day_data: ProjectedActivity[];
  itinerary_by_time_of_the_day: ProjectedActivity[];
}

export interface ProjectedItinerary {
  kind: string | null;
  version: number | null;
  singleSelectedDestination: true;
  singleCity: boolean;
  preText: string;
  postText: string;
  description: string;
  complete_itinerary_description: string;
  number_of_days: number;
  noOfDays: number;
  numberOfdays: number;
  itinerary_version: number;
  complete_itinerary_flights_cost: string;
  complete_itinerary_hotels_cost: string;
  country: string;
  tripName: string;
  destinationList: any[];
  [key: string]: unknown;
}

interface CityMeta {
  city_name: string;
  city_state: string;
  city_country: string;
  country_code: string;
  unsplash_image_prompts?: string[];
}

function asText(value: unknown): string {
  return value === null || value === undefined ? "" : String(value);
}

function asTextList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string" && item.length > 0);
}

function toNum(value: unknown): number | null {
  const n = typeof value === "number" ? value : parseInt(asText(value), 10);
  return Number.isFinite(n) ? n : null;
}

function formatCost(value: unknown): string {
  const text = asText(value).trim();
  if (!text) return "";
  return text.startsWith("$") ? text : `$${text}`;
}

function safeItineraryVersion(value: unknown): number {
  const n = Number(asText(value).split(".")[0]);
  return Number.isFinite(n) ? n : 1;
}

function childrenOf(state: StreamState, node: StreamNode): StreamNode[] {
  const out: StreamNode[] = [];
  for (const id of node.childIds) {
    const child = state.byId[id];
    if (child) out.push(child);
  }
  return out;
}

function rootNode(state: StreamState, tag: string): StreamNode | undefined {
  let match: StreamNode | undefined;
  for (const id of state.rootIds) {
    const node = state.byId[id];
    if (node && node.tag === tag) match = node;
  }
  return match;
}

function findChild(state: StreamState, node: StreamNode, tag: string): StreamNode | undefined {
  return childrenOf(state, node).find((child) => child.tag === tag);
}

function parseCityMeta(payload: unknown): CityMeta[] {
  if (!Array.isArray(payload)) return [];
  return payload.map((entry) => {
    if (typeof entry === "string") {
      return { city_name: entry, city_state: "", city_country: "", country_code: "" };
    }
    const obj = (entry ?? {}) as Record<string, unknown>;
    return {
      city_name: asText(obj.city_name),
      city_state: asText(obj.city_state),
      city_country: asText(obj.city_country),
      country_code: asText(obj.country_code),
      unsplash_image_prompts: asTextList(obj.unsplash_image_prompts),
    };
  });
}

function mapActivity(state: StreamState, actNode: StreamNode): ProjectedActivity {
  const geo: Record<string, unknown> = {};
  for (const child of childrenOf(state, actNode)) {
    if (
      (child.tag === "zv:leg" || child.tag === "zv:stay") &&
      child.payload &&
      typeof child.payload === "object" &&
      !Array.isArray(child.payload)
    ) {
      Object.assign(geo, child.payload as Record<string, unknown>);
    }
  }
  return {
    ...geo,
    id: actNode.id,
    time_of_the_day: asText(actNode.attrs.time),
    activity_type: asText(actNode.attrs.type),
    description: actNode.text,
  };
}

function mergeByTime(acts: ProjectedActivity[], dayId: string): ProjectedActivity[] {
  const order: string[] = [];
  const buckets: Record<string, ProjectedActivity> = {};
  for (const act of acts) {
    const time = act.time_of_the_day || "";
    const existing = buckets[time];
    if (existing) {
      existing.description = existing.description
        ? `${existing.description}\n\n${act.description}`
        : act.description;
    } else {
      buckets[time] = { ...act, id: `${dayId}:${time}` };
      order.push(time);
    }
  }
  return order.map((time) => buckets[time]);
}

function mapDay(state: StreamState, dayNode: StreamNode): ProjectedDay {
  let blurb = "";
  const rawActs: ProjectedActivity[] = [];
  for (const child of childrenOf(state, dayNode)) {
    if (child.tag === "zv:say") blurb = child.text;
    else if (child.tag === "zv:act") rawActs.push(mapActivity(state, child));
  }
  const n = toNum(dayNode.attrs.n);
  return {
    id: dayNode.id,
    n,
    day: n !== null ? `Day ${n}` : "",
    date: asText(dayNode.attrs.date),
    city_name: asText(dayNode.attrs.city),
    state: asText(dayNode.attrs.state),
    country: asText(dayNode.attrs.country),
    cc: asText(dayNode.attrs.cc),
    lat: asText(dayNode.attrs.lat),
    lng: asText(dayNode.attrs.lng),
    day_itinerary_description: blurb,
    itinerary_by_time_of_the_day_data: rawActs,
    itinerary_by_time_of_the_day: mergeByTime(rawActs, dayNode.id),
  };
}

function buildCity(index: number, meta: CityMeta | undefined, days: ProjectedDay[]): any {
  const first = days[0];
  return {
    ...DESTINATION_LIST_DATA,
    id: `city-${index}`,
    city_name: meta?.city_name || first?.city_name || "",
    cityName: meta?.city_name || first?.city_name || "",
    city_state: meta?.city_state || first?.state || "",
    city_country: meta?.city_country || first?.country || "",
    city_country_code: meta?.country_code || first?.cc || "",
    unsplash_image_prompts: meta?.unsplash_image_prompts ?? [],
    tripDetails: days.map((day) => ({
      ...TEMP_TRIP_DETAILS_DATA,
      day: day.day,
      date: day.date,
      city_name: day.city_name,
      day_itinerary_description: day.day_itinerary_description,
      itinerary_by_time_of_the_day_data: day.itinerary_by_time_of_the_day_data,
      itinerary_by_time_of_the_day: day.itinerary_by_time_of_the_day,
    })),
  };
}

function placeKey(name: string): string {
  return name.split(",")[0].replace(/\p{P}+/gu, " ").replace(/\s+/g, " ").trim().toLowerCase();
}

function isDepartureName(name: string, departureKey: string): boolean {
  return departureKey !== "" && placeKey(name) === departureKey;
}

function firstDestinationKey(days: ProjectedDay[], departureKey: string): string {
  for (const day of days) {
    const name = day.city_name.trim();
    if (name.length > 0 && !isDepartureName(name, departureKey)) {
      return `${name.toLowerCase()}|${day.cc.trim().toLowerCase()}`;
    }
  }
  return "";
}

function labelMetaForGroup(days: ProjectedDay[], departureKey: string): CityMeta | undefined {
  const labelDay =
    days.find((d) => {
      const name = d.city_name.trim();
      return name.length > 0 && !isDepartureName(name, departureKey);
    }) ?? days[0];
  if (!labelDay) return undefined;
  return {
    city_name: labelDay.city_name,
    city_state: labelDay.state,
    city_country: labelDay.country,
    country_code: labelDay.cc,
  };
}

function groupByDayCity(days: ProjectedDay[], departureKey: string): any[] {
  const order: string[] = [];
  const groups: Record<string, ProjectedDay[]> = {};
  const anchorKey = firstDestinationKey(days, departureKey);
  let lastKey = "";
  for (const day of days) {
    const name = day.city_name.trim();
    const hasCity = name.length > 0 && !isDepartureName(name, departureKey);
    let key = `${name.toLowerCase()}|${day.cc.trim().toLowerCase()}`;
    if (!hasCity) key = lastKey || anchorKey || key;
    if (!groups[key]) {
      groups[key] = [];
      order.push(key);
    }
    groups[key].push(day);
    lastKey = key;
  }
  return order.map((key, index) => buildCity(index, labelMetaForGroup(groups[key], departureKey), groups[key]));
}

function matchDeclaredIndex(meta: CityMeta[], day: ProjectedDay): number {
  const name = day.city_name.trim().toLowerCase();
  const cc = day.cc.trim().toLowerCase();
  if (!name) return -1;
  return meta.findIndex((m) => {
    if (m.city_name.trim().toLowerCase() !== name) return false;
    const mcc = m.country_code.trim().toLowerCase();
    return !mcc || !cc || mcc === cc;
  });
}

function groupCities(days: ProjectedDay[], meta: CityMeta[], departureKey: string, stillPlanning: boolean): any[] {
  if (meta.length === 0) return groupByDayCity(days, departureKey);

  const buckets: ProjectedDay[][] = meta.map(() => []);
  let current = 0;
  for (const day of days) {
    const matched = matchDeclaredIndex(meta, day);
    if (matched >= 0) current = matched;
    buckets[current].push(day);
  }
  return meta
    .map((m, index) => ({ m, index, days: buckets[index] }))
    .filter((entry) => stillPlanning || entry.days.length > 0)
    .map((entry, position) => buildCity(position, entry.m, entry.days));
}

function departureCityKey(payload: unknown): string {
  if (!payload || typeof payload !== "object") return "";
  const p = payload as Record<string, any>;
  return placeKey(asText(p?.departure?.city?.name));
}

function mapTripPrefs(payload: unknown): Record<string, unknown> {
  if (!payload || typeof payload !== "object") return {};
  const p = payload as Record<string, any>;
  const city = p?.departure?.city;
  const fromStr = city
    ? `${asText(city.name)}, ${asText(city.state)}, ${asText(city.country)}`
    : "";
  return {
    ...p,
    from_latitude: city?.latitude,
    from_longitude: city?.longitude,
    start_date: p?.departure?.date,
    trip_type: p?.flights?.trip_type,
    from: fromStr,
    fromCity: fromStr,
    flight_cabin_class: p?.flights?.cabin_class,
    number_of_stops: p?.flights?.number_of_stops,
    flight_refundable: p?.flights?.refundable,
    flight_day_part: p?.flights?.time_of_day,
    departure_airport: p?.flights?.departure_airports,
    arrival_airport: p?.flights?.arrival_airports,
    airlines: p?.flights?.airlines,
    number_of_rooms: p?.hotels?.number_of_rooms,
    hotel_refundable: p?.hotels?.refundable,
    hotel_star_ratings: p?.hotels?.star_ratings,
    hotels: p?.hotels?.hotel_brands ?? [],
    hotel_name: p?.hotels?.hotel_name ?? "",
    accommodation_type: p?.hotels?.accommodation_type ?? "",
  };
}

export function projectItinerary(state: StreamState): ProjectedItinerary {
  const intro = rootNode(state, "zv:intro");
  const outro = rootNode(state, "zv:outro");
  const prefsNode = rootNode(state, "zv:trip-prefs");
  const itinNode = rootNode(state, "zv:itinerary");

  const declaredMeta = itinNode ? parseCityMeta(findChild(state, itinNode, "zv:cities")?.payload) : [];
  const departureKey = departureCityKey(prefsNode?.payload);
  const meta = declaredMeta.filter((m) => !isDepartureName(m.city_name, departureKey));
  const summaryNode = itinNode ? findChild(state, itinNode, "zv:summary") : undefined;
  const dayNodes = itinNode
    ? childrenOf(state, itinNode).filter((node) => node.tag === "zv:day")
    : [];
  const days = dayNodes.map((node) => mapDay(state, node));
  const destinationList = groupCities(days, meta, departureKey, itinNode?.status === "open");

  const attrs = itinNode?.attrs ?? {};
  const description = summaryNode?.text ?? "";
  const declaredDays = Number(attrs.days);
  const totalDays = Number.isFinite(declaredDays) && declaredDays > 0 ? declaredDays : days.length;

  return {
    ...mapTripPrefs(prefsNode?.payload),
    kind: state.kind,
    version: state.version,
    singleSelectedDestination: true,
    singleCity: destinationList.length === 1,
    preText: intro?.text ?? "",
    postText: outro?.text ?? "",
    description,
    complete_itinerary_description: description,
    number_of_days: totalDays,
    noOfDays: totalDays,
    numberOfdays: totalDays,
    itinerary_version: safeItineraryVersion(attrs.version),
    complete_itinerary_flights_cost: formatCost(attrs.flights_cost),
    complete_itinerary_hotels_cost: formatCost(attrs.hotels_cost),
    country: asText(attrs.country),
    tripName: destinationList
      .map((city) => city.city_name)
      .filter((name: string) => name)
      .join(", "),
    destinationList,
  };
}
